import { useState, useCallback, useEffect } from 'react';
import { supabase } from './useSupabase';
import { companyData } from '@/mocks/company';
import { showToast } from '@/components/Toast';

export interface CompanyData {
  name: string;
  foundedYear: number;
  responsible: string;
  cnpj: string;
  city: string;
  state: string;
  phone1: string;
  phone2: string;
  email: string;
  website: string;
  workHours: {
    weekday: string;
    saturday: string;
  };
}

const STORAGE_KEY = 'company-data';

function loadCompanyData(): CompanyData {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      return JSON.parse(raw) as CompanyData;
    }
  } catch {
    console.warn('Erro ao carregar dados do localStorage');
  }
  return { ...companyData };
}

function saveCompanyData(data: CompanyData): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    console.warn('Erro ao salvar dados no localStorage');
  }
}

export function useCompanyData() {
  const [data, setData] = useState<CompanyData>(loadCompanyData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCompany = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: company, error } = await supabase
        .from('companies')
        .select('*')
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      if (company) {
        const mapped: CompanyData = {
          name: company.name || companyData.name,
          foundedYear: company.founded_year || companyData.foundedYear,
          responsible: companyData.responsible,
          cnpj: company.cnpj || companyData.cnpj,
          city: company.city || companyData.city,
          state: company.state || companyData.state,
          phone1: company.phone1 || companyData.phone1,
          phone2: company.phone2 || companyData.phone2,
          email: company.email || companyData.email,
          website: company.website || companyData.website,
          workHours: companyData.workHours,
        };
        setData(mapped);
        saveCompanyData(mapped);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados da empresa');
      showToast('Erro ao carregar dados da empresa. Usando dados locais.', 'warning');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompany();
  }, [fetchCompany]);

  const updateData = useCallback(async (newData: CompanyData) => {
    setData(newData);
    saveCompanyData(newData);
    try {
      const { data: company } = await supabase.from('companies').select('id').limit(1).maybeSingle();
      if (company?.id) {
        await supabase
          .from('companies')
          .update({
            name: newData.name,
            founded_year: newData.foundedYear,
            cnpj: newData.cnpj,
            city: newData.city,
            state: newData.state,
            phone1: newData.phone1,
            phone2: newData.phone2,
            email: newData.email,
            website: newData.website,
          })
          .eq('id', company.id);
        showToast('Dados da empresa salvos com sucesso!', 'success');
      }
    } catch (err) {
      showToast('Erro ao salvar dados da empresa. Mantidos localmente.', 'warning');
    }
  }, []);

  const updateField = useCallback(<K extends keyof CompanyData>(
    key: K,
    value: CompanyData[K]
  ) => {
    const newData = { ...data, [key]: value };
    updateData(newData);
  }, [data, updateData]);

  const resetToDefault = useCallback(() => {
    const defaultData = { ...companyData };
    setData(defaultData);
    saveCompanyData(defaultData);
    showToast('Dados restaurados para o padrão.', 'info');
  }, []);

  return { data, loading, error, updateData, updateField, resetToDefault };
}